// Predictive insights helpers — pure functions, no React deps.

const MS_PER_DAY = 86400000;

export function getPolicyYearWindow(today, policyStartDate) {
  if (policyStartDate) {
    let start = new Date(policyStartDate + "T00:00:00");
    if (!isNaN(start.getTime()) && start <= today) {
      while (today.getTime() >= start.getTime() + 365 * MS_PER_DAY) {
        start = new Date(start.getTime() + 365 * MS_PER_DAY);
      }
      return { start, end: new Date(start.getTime() + 365 * MS_PER_DAY), usingPolicy: true };
    }
  }
  const start = new Date(today.getFullYear(), 0, 1);
  const end = new Date(today.getFullYear() + 1, 0, 1);
  return { start, end, usingPolicy: false };
}

/**
 * Project year-end mileage based on current daily rate.
 * Returns { km, dailyRate, overCap, deltaKm }.
 */
export function projectYearEnd({ yearUsedKm, yearlyLimitKm, today, policyStartDate }) {
  const { start, end } = getPolicyYearWindow(today, policyStartDate);
  const daysInYear = Math.max(1, Math.round((end - start) / MS_PER_DAY));
  const daysElapsed = Math.max(1, Math.round((today - start) / MS_PER_DAY));
  const dailyRate = yearUsedKm / daysElapsed;
  const projectedKm = dailyRate * daysInYear;
  return {
    km: projectedKm,
    dailyRate,
    overCap: yearlyLimitKm > 0 && projectedKm > yearlyLimitKm,
    deltaKm: yearlyLimitKm > 0 ? projectedKm - yearlyLimitKm : 0,
  };
}

/**
 * Compute end-of-month total based on current month's daily rate.
 */
export function projectMonthEnd({ monthUsedKm, dayOfMonth, daysInMonth }) {
  if (dayOfMonth <= 0) return monthUsedKm;
  const dailyRate = monthUsedKm / dayOfMonth;
  return dailyRate * daysInMonth;
}

/**
 * Build a 12-month forecast: returns past actuals + future projected values.
 * Future months use today's daily rate × days in that month.
 */
export function buildMonthlyForecast({ monthly, today, daysInPolicyYear, dailyRate }) {
  // Build full 12-month frame for the policy year (Jan..Dec).
  const year = today.getFullYear();
  const out = [];
  for (let m = 0; m < 12; m++) {
    const monthStart = new Date(year, m, 1);
    const monthEnd = new Date(year, m + 1, 0);
    const daysInMonth = monthEnd.getDate();
    const ymKey = `${year}-${String(m + 1).padStart(2, "0")}`;
    const actualEntry = (monthly || []).find((x) => x.month === ymKey);
    const isPast = monthEnd < today && !actualEntry;
    const isFuture = monthStart > today;
    const isCurrent = today >= monthStart && today <= monthEnd;

    if (actualEntry || isPast) {
      out.push({
        month: ymKey,
        label: monthStart.toLocaleString(undefined, { month: "short", year: "2-digit" }),
        actual: actualEntry ? actualEntry.distance_km : 0,
        forecast: 0,
        isCurrent,
      });
    } else if (isCurrent) {
      const actual = actualEntry ? actualEntry.distance_km : 0;
      // Projected end-of-month
      const dayOfMonth = today.getDate();
      const monthDailyRate = dayOfMonth > 0 ? actual / dayOfMonth : 0;
      const projected = monthDailyRate * daysInMonth;
      out.push({
        month: ymKey,
        label: monthStart.toLocaleString(undefined, { month: "short", year: "2-digit" }),
        actual,
        forecast: Math.max(0, projected - actual),
        isCurrent: true,
      });
    } else if (isFuture) {
      const forecastKm = dailyRate * daysInMonth;
      out.push({
        month: ymKey,
        label: monthStart.toLocaleString(undefined, { month: "short", year: "2-digit" }),
        actual: 0,
        forecast: forecastKm,
        isFuture: true,
      });
    }
  }
  return out;
}

/**
 * Generate human-readable insights from trips array.
 */
export function buildInsights({ trips, yearUsedKm, yearlyLimitKm, today, policyStartDate }) {
  const out = [];
  if (!trips || trips.length === 0) return out;

  const MS = 86400000;
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  const tripsThisMonth = trips.filter((t) => {
    const d = new Date(t.date + "T00:00:00");
    return d >= monthStart && d <= today;
  });
  const tripsLastMonth = trips.filter((t) => {
    const d = new Date(t.date + "T00:00:00");
    return d >= prevMonthStart && d <= prevMonthEnd;
  });

  // 1. Avg trip distance MoM
  if (tripsLastMonth.length >= 2 && tripsThisMonth.length >= 2) {
    const avgThis =
      tripsThisMonth.reduce((a, t) => a + t.distance_km, 0) / tripsThisMonth.length;
    const avgPrev =
      tripsLastMonth.reduce((a, t) => a + t.distance_km, 0) / tripsLastMonth.length;
    if (avgPrev > 0) {
      const pct = ((avgThis - avgPrev) / avgPrev) * 100;
      out.push({
        kind: "avg_trip",
        title: `Your average trip distance is ${pct >= 0 ? "up" : "down"} ${Math.abs(pct).toFixed(0)}%`,
        sub: "vs last month",
        sentiment: pct > 15 ? "warn" : pct < -15 ? "good" : "neutral",
      });
    }
  }

  // 2. Weekend share
  if (tripsThisMonth.length >= 5) {
    const weekend = tripsThisMonth.filter((t) => {
      const d = new Date(t.date + "T00:00:00").getDay();
      return d === 0 || d === 6;
    });
    const pct = (weekend.length / tripsThisMonth.length) * 100;
    if (pct >= 40) {
      out.push({
        kind: "weekend",
        title: "Weekends are your most active",
        sub: `${pct.toFixed(0)}% of trips happen on Sat or Sun`,
        sentiment: "neutral",
      });
    } else if (pct <= 20) {
      out.push({
        kind: "weekday",
        title: "Weekdays drive your mileage",
        sub: `Only ${pct.toFixed(0)}% of trips on weekends`,
        sentiment: "neutral",
      });
    }
  }

  // 3. Annual limit pace
  if (yearlyLimitKm > 0 && yearUsedKm > 0) {
    const usedPct = (yearUsedKm / yearlyLimitKm) * 100;
    const { start, end } = getPolicyYearWindow(today, policyStartDate);
    const daysInYear = Math.max(1, Math.round((end - start) / MS));
    const daysElapsed = Math.max(1, Math.round((today - start) / MS));
    const idealPct = (daysElapsed / daysInYear) * 100;
    const diff = usedPct - idealPct;
    out.push({
      kind: "limit_pace",
      title: `You've used ${usedPct.toFixed(0)}% of your annual limit`,
      sub:
        Math.abs(diff) < 1
          ? "Right on your ideal pace"
          : `You're ${Math.abs(diff).toFixed(0)}% ${diff > 0 ? "ahead of" : "behind"} your ideal pace`,
      sentiment: diff > 5 ? "warn" : diff < -5 ? "good" : "neutral",
    });
  }

  // 4. Longest trip this month
  if (tripsThisMonth.length > 0) {
    const longest = tripsThisMonth.reduce(
      (a, b) => (b.distance_km > a.distance_km ? b : a),
      tripsThisMonth[0]
    );
    if (longest.distance_km > 0) {
      const d = new Date(longest.date + "T00:00:00");
      const fmtDate = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      out.push({
        kind: "longest",
        title: "Your longest trip this month",
        sub: `${longest.distance_km.toFixed(0)} km on ${fmtDate}${longest.notes ? " · " + longest.notes : ""}`,
        sentiment: "neutral",
      });
    }
  }

  return out;
}

/**
 * Annual pace status — derived EXCLUSIVELY from projected year-end mileage
 * vs the annual allowance. Single source of truth for dashboard pace messaging.
 *  - On Pace   : projected < 95% of annual limit
 *  - Watch Pace: 95% <= projected <= 100% of annual limit
 *  - Over Pace : projected > 100% of annual limit
 */
export function computeAnnualPaceStatus({ projectedKm, annualLimitKm }) {
  if (
    !annualLimitKm ||
    annualLimitKm <= 0 ||
    projectedKm == null ||
    !isFinite(projectedKm)
  ) {
    return { status: "unknown", label: "—", projectedPct: 0 };
  }
  const ratio = projectedKm / annualLimitKm;
  const projectedPct = ratio * 100;
  if (ratio > 1.0) return { status: "over", label: "Over Pace", projectedPct };
  if (ratio >= 0.95) return { status: "watch", label: "Watch Pace", projectedPct };
  return { status: "on", label: "On Pace", projectedPct };
}

/**
 * Safe Daily Average — the per-day distance the user can drive over the
 * REMAINDER of the policy year while still finishing within the annual
 * allowance. Derived from annual allowance only; ignores monthly performance.
 */
export function computeSafeDailyAverage({ remainingAnnualKm, remainingPolicyDays }) {
  if (!remainingPolicyDays || remainingPolicyDays <= 0) return 0;
  return Math.max(0, remainingAnnualKm) / remainingPolicyDays;
}

/**
 * Compute 7d vs prior-7d trend deltas from a trips array.
 */
export function computeWeeklyTrends(trips) {
  if (!trips || trips.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t7 = new Date(today.getTime() - 7 * MS_PER_DAY);
  const t14 = new Date(today.getTime() - 14 * MS_PER_DAY);

  const thisWeek = [];
  const lastWeek = [];
  for (const t of trips) {
    const d = new Date(t.date + "T00:00:00");
    if (d > today) continue;
    if (d > t7) thisWeek.push(t);
    else if (d > t14) lastWeek.push(t);
  }

  const sumKm = (arr) => arr.reduce((a, t) => a + (t.distance_km || 0), 0);
  const delta = (a, b) => {
    if (!isFinite(a) || !isFinite(b)) return null;
    if (b === 0) return a === 0 ? 0 : null; // null = "Need more driving history"
    return ((a - b) / b) * 100;
  };

  const twKm = sumKm(thisWeek);
  const lwKm = sumKm(lastWeek);
  const twAvg = thisWeek.length ? twKm / thisWeek.length : 0;
  const lwAvg = lastWeek.length ? lwKm / lastWeek.length : 0;

  return {
    total: delta(twKm, lwKm),
    trips: delta(thisWeek.length, lastWeek.length),
    avgPerTrip: delta(twAvg, lwAvg),
    avgPerDay: delta(twKm / 7, lwKm / 7),
  };
}
